import React, { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { txtdb } from "../../firebase-config";
import { NavLink, useNavigate } from "react-router-dom";
import { IoIosLogOut } from "react-icons/io";
import { AiOutlineMenu } from "react-icons/ai";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase-config";
import logo from "../../stock/nexus/logo.png";

// --- Sub-Component for individual cards to handle local input state ---
const TradeCard = ({ trade, onUpdate }) => {
  const [customAmount, setCustomAmount] = useState("");

  const quickAmounts = [10, 50, 100, 1000];

  const handleManualSubmit = () => {
    if (!customAmount) return;
    const value = parseFloat(customAmount);
    if (isNaN(value)) return;
    
    // Calculate new total (Current + Input)
    const newTotal = parseFloat(trade.currentAmount) + value;
    onUpdate(trade.id, trade.userId, newTotal);
    setCustomAmount(""); // Reset input
  };

  const handleQuickAdd = (amount) => {
    const newTotal = parseFloat(trade.currentAmount) + amount;
    onUpdate(trade.id, trade.userId, newTotal);
  };

  return (
    <div className="trade-card">
      <div className="card-header">
        <div className="status-indicator">Active</div>
        <h3>{trade.traderName || "Unnamed Trader"}</h3>
      </div>

      <div className="card-body">
        <div className="info-row">
          <span>User</span>
          <strong>{trade.userName}</strong>
        </div>
        <div className="info-row">
          <span>Asset</span>
          <strong>{trade.asset}</strong>
        </div>
        <div className="info-row">
          <span>Duration</span>
          <strong>{trade.durationDays} Days</strong>
        </div>
        
        <div className="balance-section">
          <span className="label">Current Equity</span>
          <div className="amount">${Number(trade.currentAmount).toFixed(2)}</div>
        </div>
      </div>

      <div className="card-actions">
        <p className="action-label">Quick Top-up</p>
        <div className="quick-buttons">
          {quickAmounts.map((amt) => (
            <button key={amt} onClick={() => handleQuickAdd(amt)}>+${amt}</button>
          ))}
        </div>

        <p className="action-label">Custom Adjustment</p>
        <div className="input-group">
          <input
            type="number"
            placeholder="Amount (e.g. 500 or -50)"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
          />
          <button className="submit-btn" onClick={handleManualSubmit}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

function CopyTrades() {
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Fetch all copy trades
  useEffect(() => {
    const fetchAllTrades = async () => {
      try {
        setLoading(true);
        const kycRef = collection(txtdb, "kycInfo");
        const usersSnapshot = await getDocs(kycRef);
        let allTrades = [];

        for (const userDoc of usersSnapshot.docs) {
          const userId = userDoc.id;
          const userData = userDoc.data();
          const copyTradesRef = collection(txtdb, "users", userId, "copyTrades");
          const tradesSnapshot = await getDocs(copyTradesRef);

          tradesSnapshot.forEach((tradeDoc) => {
            allTrades.push({
              id: tradeDoc.id,
              userId,
              userName: `${userData.firstName} ${userData.lastName}`,
              ...tradeDoc.data(),
            });
          });
        }
        setTrades(allTrades);
      } catch (err) {
        console.error("Error fetching copy trades:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllTrades();
  }, []);

  // Update trade amount function
  const updateTradeAmount = async (tradeId, userId, newTotal) => {
    try {
      const tradeRef = doc(txtdb, `users/${userId}/copyTrades/${tradeId}`);
      await updateDoc(tradeRef, { currentAmount: parseFloat(newTotal) });
      
      setTrades((prev) =>
        prev.map((trade) =>
          trade.id === tradeId && trade.userId === userId
            ? { ...trade, currentAmount: parseFloat(newTotal) }
            : trade
        )
      );
      // Optional: Add a toast notification here instead of alert
    } catch (err) {
      console.error("Error updating amount:", err);
      alert("Failed to update amount.");
    }
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => navigate("/login"))
      .catch((error) => console.error("Logout error:", error));
  };

  return (
    <div className='layout admin'>
      <div className="content">
        <div className="transactions-container container">
          
          {/* Header Navigation */}
          <div className="navigation-container">
            <NavLink className="mobile">
              <img src={logo} alt="nexusforgefx" />
              <p className='name'>Copy Trades</p>
            </NavLink>
            <p className='desktop'>Copy Trades Management</p>
            
            <div className="actions desktop">
              <div className="quick" onClick={() => setOpen(!open)}>
                Admin Pages <AiOutlineMenu />
              </div>
              <div className="notif" onClick={handleLogout}>
                <IoIosLogOut className="icon" title="logout" />
              </div>
            </div>

            <div className="actions mobile">
              <div className="notif" onClick={handleLogout}>
                <IoIosLogOut className="icon" title="logout" />
              </div>
              <div className="nav" onClick={() => setOpen(!open)}>
                <AiOutlineMenu className='icon' />
              </div>
            </div>
          </div>

          {open && (
               <div className="admin-nav">
                         <NavLink to="/pendingDeposits">Pending Deposits</NavLink>
                         <NavLink to="/all-users">All Users</NavLink>
                         <NavLink to="/copytrades">Active Copy Trades</NavLink>
                         <NavLink to="/adminKycReview">KYC Reviews</NavLink>
                         <NavLink to="/admin-wallets">Wallet Management</NavLink>
                       </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="pages-content" onClick={() => setOpen(false)}>
          <div className="copytrades-page">
            {loading ? (
              <p className="load">Loading active trades...</p>
            ) : trades.length === 0 ? (
              <p className="load">No active copy trades found.</p>
            ) : (
              <div className="trades-grid">
                {trades.map((trade) => (
                  <TradeCard 
                    key={trade.id} 
                    trade={trade} 
                    onUpdate={updateTradeAmount} 
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CopyTrades;